import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// CSC-288 master-detail Quotation workspace state.
//
// This store owns LIST-LEVEL navigation: which row is selected, which rows are
// pinned for quick comparison, the master-panel resize ratio, the search
// string, and the status filter. The existing `quotationDraftStore` keeps
// owning OPEN-DRAFT lifecycle (drafts[], activeId, dirty tracking, save state)
// — the detail panel reaches into it via `loadDraft` when a row is clicked.
//
// The workspace is the sole quotation edit/post surface (86d3k9589); the legacy
// /quotations/compose route was retired and now redirects here.

export type WorkspaceStatusFilter =
  | 'active' // Draft + AutoDraft (default — inbox feel)
  | 'all'
  | 'drafts'
  | 'autodrafts'
  | 'submitted'
  | 'sent'
  | 'archived';

const PIN_CAP = 5;

// Fixed pixel range — the queue list reads better at a tight, predictable
// width regardless of viewport, and the detail panel always wants the bulk
// of horizontal space. 40px of drag range still lets power users nudge the
// split, but viewport changes won't reflow the master columns.
const MASTER_WIDTH_MIN_PX = 290;
const MASTER_WIDTH_MAX_PX = 330;
const MASTER_WIDTH_DEFAULT_PX = 310;

interface QuotationWorkspaceState {
  /** Server id of the row currently rendered in the detail panel. Null when
   *  no row is selected (the panel renders an EmptyState). */
  selectedId: number | null;
  /** Server ids the user has pinned for fast comparison via the chip strip.
   *  FIFO-evict on the 6th pin so the strip can't grow unbounded. */
  pinnedIds: number[];
  /** Workspace split width for the master pane, in pixels. Clamped to
   *  [290, 330] — fixed-px so the list columns don't reflow with the
   *  viewport. The detail pane absorbs all remaining horizontal space. */
  masterPanelWidthPx: number;
  /** Free-text search applied to customer name / project / quotation number.
   *  Persisted so a refresh keeps the user in their current triage view. */
  quickFilter: string;
  /** High-level status filter pill (Active by default — inbox-style triage). */
  statusFilter: WorkspaceStatusFilter;
  /** Multi-select for bulk actions. Distinct from `selectedId` (which drives
   *  the detail panel). Per-row checkbox flips membership; clicking the row
   *  body still drives selectedId without touching the multi-select set. */
  multiSelectedIds: number[];
  /** CSC-289: when the user opens a previously-unread row, the master list
   *  stamps that row's id here. The detail panel watches this and renders an
   *  inline "this is new" alert banner above the form. Cleared on dismiss
   *  and on opening any read row, so navigating away doesn't leave a stale
   *  banner waiting to re-show. Not persisted — ephemeral, resets on reload. */
  unreadAlertQuotationId: number | null;

  setSelectedId: (id: number | null) => void;
  togglePin: (id: number) => void;
  isPinned: (id: number) => boolean;
  setMasterPanelWidthPx: (px: number) => void;
  setQuickFilter: (q: string) => void;
  setStatusFilter: (f: WorkspaceStatusFilter) => void;
  toggleMultiSelect: (id: number) => void;
  isMultiSelected: (id: number) => boolean;
  clearMultiSelect: () => void;
  /** Replace the entire multi-select set. Used by the master list's stale-id
   *  cleanup effect to prune ids that no longer appear in `rows`. */
  setMultiSelectedIds: (ids: number[]) => void;
  setUnreadAlertQuotationId: (id: number | null) => void;
}

export const useQuotationWorkspaceStore = create<QuotationWorkspaceState>()(
  persist(
    (set, get) => ({
      selectedId: null,
      pinnedIds: [],
      masterPanelWidthPx: MASTER_WIDTH_DEFAULT_PX,
      quickFilter: '',
      statusFilter: 'active',
      multiSelectedIds: [],
      unreadAlertQuotationId: null,

      setSelectedId: (id) => set({ selectedId: id }),

      togglePin: (id) => {
        const current = get().pinnedIds;
        if (current.includes(id)) {
          set({ pinnedIds: current.filter((x) => x !== id) });
          return;
        }
        // FIFO-evict the oldest pin when we're at the cap so the strip stays
        // single-row across reasonable viewport widths.
        const next = current.length >= PIN_CAP ? [...current.slice(1), id] : [...current, id];
        set({ pinnedIds: next });
      },

      isPinned: (id) => get().pinnedIds.includes(id),

      toggleMultiSelect: (id) => {
        const current = get().multiSelectedIds;
        if (current.includes(id)) {
          set({ multiSelectedIds: current.filter((x) => x !== id) });
        } else {
          set({ multiSelectedIds: [...current, id] });
        }
      },

      isMultiSelected: (id) => get().multiSelectedIds.includes(id),

      clearMultiSelect: () => set({ multiSelectedIds: [] }),

      setMultiSelectedIds: (ids) => set({ multiSelectedIds: ids }),

      setMasterPanelWidthPx: (px) => {
        const clamped = Math.min(MASTER_WIDTH_MAX_PX, Math.max(MASTER_WIDTH_MIN_PX, px));
        set({ masterPanelWidthPx: clamped });
      },

      setQuickFilter: (q) => set({ quickFilter: q }),

      setStatusFilter: (f) => set({ statusFilter: f }),

      setUnreadAlertQuotationId: (id) => set({ unreadAlertQuotationId: id }),
    }),
    {
      name: 'quotation-workspace',
      storage: createJSONStorage(() => sessionStorage),
      // Don't persist `selectedId` — it lives in the URL (?id=) and rehydrating
      // from session would race the URL parser. Same reason `quickFilter` /
      // `statusFilter` could be URL-driven, but we persist them so the user's
      // last view sticks across tab close + reopen.
      partialize: (state) => ({
        pinnedIds: state.pinnedIds,
        masterPanelWidthPx: state.masterPanelWidthPx,
        quickFilter: state.quickFilter,
        statusFilter: state.statusFilter,
      }),
    },
  ),
);
